import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";
import { useGetMachinesBySiteId } from "hooks/machines/useGetMachinesBySiteId";
import { SeverityTypeContext } from "contexts/SeverityTypeContext";
import { usePutRuleLevel } from "hooks/rule-levels/usePutRuleLevel";
import { RuleLevelDialog } from "components/rules-management/RuleLevelDialog/RuleLevelDialog";
import { useErrorMessage } from "components/rules-management/RuleLevelDialog/useErrorMessage";
import { useSitesQuery } from "hooks/queries/useSitesQuery";

export const EditRuleLevelDialog = ({
  ruleId,
  ruleLevel,
  open,
  onClose,
  onSuccess
}) => {
  const [siteId, setSiteId] = useState();
  const [initialValues, setInitialValues] = useState();
  const {
    errorMessage,
    errorAcknowledged,
    onError,
    onErrorAcknowledged
  } = useErrorMessage();

  const {
    loading: loadingSeverityTypes,
    ruleSeverityTypes: severityTypes
  } = useContext(SeverityTypeContext);

  const { isLoading: loadingSites, data: _sites } = useSitesQuery();
  // ensure variable is defined for typescript
  const sites = useMemo(() => _sites?.results ?? [], [_sites]);

  const { loading: loadingMachines, machines } = useGetMachinesBySiteId(siteId);
  const { loading, error, putRuleLevelCallback } = usePutRuleLevel(
    ruleLevel?.rule_level_id
  );

  const getNonFieldError = errorJson => {
    const validationError = errorJson?.non_field_errors;
    if (validationError) {
      return validationError.length > 0 ? validationError[0] : undefined;
    }
  };

  const handleSuccess = () => {
    onErrorAcknowledged();
    onSuccess();
  };

  const handleClose = () => {
    onErrorAcknowledged();
    onClose();
  };

  const handleSubmit = (
    { levelType, level, site, machine, recommendation },
    resetForm
  ) => {
    const tempRuleLevel = {
      rule: ruleId,
      level_type: levelType.id,
      level,
      site: site?.id,
      machine: machine?.id,
      recommendation
    };

    putRuleLevelCallback(tempRuleLevel).then(
      _success => {
        resetForm();
        handleSuccess();
      },
      errorResponse => {
        errorResponse.json().then(errorJson => {
          onError(getNonFieldError(errorJson));
        });
      }
    );
  };

  //reset the siteId every time the rules level changes
  useEffect(() => {
    setSiteId(ruleLevel?.site_id);
  }, [ruleLevel]);

  //clear the initial values every time the dialog is closed
  useEffect(() => {
    if (!open) {
      setInitialValues(undefined);
    }
  }, [open]);

  //set the initial values if they haven't already been set
  useEffect(() => {
    if (
      open &&
      !initialValues &&
      //machine should either be null or it should only be initialised when the machines hook
      // is using the correct site id and the array has values
      (!ruleLevel?.machine_id ||
        (siteId === ruleLevel.site_id && !loadingMachines && machines)) &&
      //site should either be null or it should only be initialised when sites has values
      (!ruleLevel.site_id || (!loadingSites && sites)) &&
      //rules level type is always required and should only be initialised when the array has values
      !loadingSeverityTypes &&
      severityTypes
    ) {
      setInitialValues({
        levelType: getInitialObjectById(
          severityTypes,
          ruleLevel?.rule_level_type_id
        ),
        level: ruleLevel?.level ?? 0,
        site: getInitialObjectById(sites, ruleLevel?.site_id),
        machine: getInitialObjectById(machines, ruleLevel?.machine_id),
        recommendation: ruleLevel.recommendation ?? ""
      });
    }
  }, [
    open,
    initialValues,
    loadingMachines,
    loadingSites,
    loadingSeverityTypes,
    severityTypes,
    ruleLevel,
    sites,
    machines,
    siteId
  ]);

  return (
    <RuleLevelDialog
      open={open}
      title="Edit Threshold"
      loading={loading || !initialValues}
      error={error && !errorAcknowledged}
      errorMessage={errorMessage}
      sites={sites}
      loadingSites={loadingSites}
      machines={machines}
      loadingMachines={loadingMachines}
      severityTypes={severityTypes}
      loadingSeverityTypes={loadingSeverityTypes}
      confirmButtonLabel="Save changes"
      handleSelectSiteId={setSiteId}
      onClose={handleClose}
      onSubmit={handleSubmit}
      initialValues={
        initialValues ?? {
          levelType: null,
          level: 0,
          site: null,
          machine: null,
          recommendation: ""
        }
      }
    />
  );
};

EditRuleLevelDialog.propTypes = {
  ruleId: PropTypes.number,
  ruleLevel: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};

/**
 * Gets an object by id from the array provided
 * @param objectArray the array to get the object from
 * @param id the id of the object to get
 * @returns {null|*} the object or null
 */
const getInitialObjectById = (objectArray, id) => {
  if (id) {
    return objectArray?.find(x => x.id === id) ?? null;
  }

  return null;
};
